import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import financeReconciliationService from '../services/financeReconciliationService';
import { FINANCE_RECONCILIATION_UPDATED_EVENT, FINANCE_RECONCILIATION_UPDATED_KEY } from '../utils/financeReconciliationEvents';

const getCurrentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};
const amountFormatter = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatAmount = value => value === null || value === undefined ? '—' : amountFormatter.format(value);
const statusLabel = status => ({ active: 'Active', inactive: 'Inactive', draft: 'Draft', submitted: 'Pending approval', approved: 'Approved', rejected: 'Rejected', historical: 'Historical' }[status] || status);
const getCurrency = provider => provider?.currency?.currency_code || provider?.currency?.code || 'Unknown';

export const MerchantStatusBadge = ({ status }) => (
  <span className={`merchant-status merchant-status--${status}`}>{statusLabel(status)}</span>
);

const Metric = ({ label, value, detail, warning = false }) => (
  <div className={`merchant-metric${warning ? ' merchant-metric--warning' : ''}`}>
    <span>{label}</span><strong>{value}</strong><small>{detail}</small>
  </div>
);

const MerchantFinancialDashboard = ({ merchant, view, canViewFinance, tickets, ticketsLoading, canViewTickets, onViewProducts, onViewPaymentGateways, onViewReports }) => {
  const navigate = useNavigate();
  const [month, setMonth] = useState('');
  const [currency, setCurrency] = useState(() => getCurrency(merchant));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(canViewFinance);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!canViewFinance) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    financeReconciliationService.getMerchantOverview(merchant._id, month ? { report_month: month } : {}).then(res => {
      if (cancelled) return;
      if (res.success) setData(res.data);
      else setError(res.message || 'Unable to load financial reports.');
    }).catch(() => {
      if (!cancelled) setError('Unable to load financial reports.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [merchant._id, month, canViewFinance, revision]);

  useEffect(() => {
    const refresh = () => setRevision(value => value + 1);
    const onStorage = event => { if (event.key === FINANCE_RECONCILIATION_UPDATED_KEY) refresh(); };
    window.addEventListener(FINANCE_RECONCILIATION_UPDATED_EVENT, refresh);
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(FINANCE_RECONCILIATION_UPDATED_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const configured = useMemo(() => (merchant.providers || []).filter(item => item.provider_id?._id).map(item => ({
    master_provider_id: item.provider_id._id,
    provider_name: item.provider_id.provider_id?.name || item.provider_id.code || 'Unknown product',
    source_name: item.provider_id.source?.name || 'Not set',
    currency_code: getCurrency(item.provider_id),
    type: item.provider_id.type,
    is_client_line: item.is_client_line,
    status: item.status || (item.is_active ? 'active' : 'inactive')
  })), [merchant.providers]);

  const products = useMemo(() => {
    const items = new Map(configured.map(item => [`${item.master_provider_id}:${item.currency_code}`, item]));
    for (const financial of data?.products || []) {
      const key = `${financial.master_provider_id}:${financial.currency_code}`;
      const metadata = configured.find(item => item.master_provider_id === financial.master_provider_id)
        || data.providers.find(item => item.master_provider_id === financial.master_provider_id);
      items.set(key, { ...metadata, status: metadata?.status || 'historical', ...financial });
    }
    return [...items.values()];
  }, [configured, data]);
  const currencyOptions = [...new Set(products.map(item => item.currency_code))].sort();
  const selectedCurrency = currencyOptions.includes(currency) ? currency : currencyOptions[0] || '';
  const visibleProducts = products.filter(item => item.currency_code === selectedCurrency);
  const total = data?.totals.find(item => item.currency_code === selectedCurrency);
  const reports = data?.reports || [];
  const approvedCount = reports.filter(report => report.status === 'approved').length;
  const pendingCount = reports.filter(report => report.status === 'submitted').length;
  const latestUpdate = data?.updated_at ? new Date(data.updated_at).toLocaleString() : null;
  const incompleteTickets = tickets.filter(ticket => ticket.status !== 'completed' && ticket.stage !== 'completed');
  const paymentGateways = merchant.payment_gateways || [];
  const showOverview = view === 'overview';
  const periodLabel = month || 'All months';
  if (!showOverview && view !== 'financial') return null;

  const renderReports = () => (
    <section className="card merchant-dashboard-section">
      <div className="merchant-section-heading">
        <div><h3>Financial Reports</h3><p>{periodLabel} · All report statuses. Open a report to view its details.</p></div>
        <span className="merchant-count">{reports.length} reports</span>
      </div>
      {reports.length === 0 ? <div className="merchant-empty">{month ? 'No financial reports for this month.' : 'No financial reports.'}</div> : (
        <div className="merchant-report-list">
          {reports.map(report => (
            <button className="merchant-report-row" key={report._id} onClick={() => navigate(`/finance-reconciliation/${report._id}`)}>
              <span><strong>{report.title || `${report.report_month} report`}</strong><small>{new Date(report.report_date).toLocaleDateString()} · Updated {new Date(report.updated_at).toLocaleDateString()}</small></span>
              <MerchantStatusBadge status={report.status} />
              <span className="merchant-report-link">View report →</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="merchant-dashboard">
      <div className="merchant-section-heading merchant-dashboard-toolbar">
        <div><h2>{showOverview ? 'Merchant Overview' : 'Financial Reports'}</h2><p>{showOverview ? 'Game providers, payment gateways and financial performance.' : 'Review this merchant’s reports and approval progress.'}</p></div>
        {canViewFinance && <div className="merchant-period-controls">
          <label>Report month<select className="input" aria-label="Report period" value={month ? 'month' : ''} onChange={event => setMonth(event.target.value ? getCurrentMonth() : '')}>
            <option value="">All</option>
            <option value="month">Specific month</option>
          </select></label>
          {month && <label>Month<input className="input" type="month" aria-label="Report month" value={month} onChange={event => setMonth(event.target.value)} /></label>}
          <label>Currency<select className="input" aria-label="Financial currency" value={selectedCurrency} onChange={event => setCurrency(event.target.value)} disabled={!currencyOptions.length}>
            {!currencyOptions.length && <option value="">No currency</option>}
            {currencyOptions.map(code => <option key={code} value={code}>{code}</option>)}
          </select></label>
        </div>}
      </div>

      {canViewFinance ? <>
        {error && <div className="merchant-dashboard-error" role="alert">{error}<button className="btn btn-secondary" onClick={() => setRevision(value => value + 1)}>Retry</button></div>}
        <div className="merchant-metrics" aria-busy={loading}>
          <Metric label="BO Win/Lose" value={loading ? '…' : formatAmount(total?.bo_winlose)} detail={`${selectedCurrency || 'Currency'} · Approved reports`} />
          <Metric label="Provider Win/Lose" value={loading ? '…' : formatAmount(total?.provider_winlose)} detail={`${selectedCurrency || 'Currency'} · Provider reported direction`} />
          <Metric label="Reconciliation Difference" value={loading ? '…' : formatAmount(total?.variance)} detail="Provider W/L aligned to BO direction − BO W/L" warning={!!total?.variance} />
          <Metric label="Approved Reports" value={loading ? '…' : error ? '—' : `${approvedCount} / ${reports.length}`} detail={loading ? 'Loading reports…' : error ? 'Reports unavailable' : `${pendingCount} pending approval · All currencies`} />
        </div>
        <p className="merchant-data-note">{loading ? 'Loading financial reports…' : error ? 'Financial amounts are unavailable. Retry to refresh.' : latestUpdate ? `Last report update: ${latestUpdate}.` : month ? 'No financial report data for this month.' : 'No financial report data.'} Amounts use approved reports only. “—” means no approved data; zero means a reported zero. W/L signs follow the financial report.</p>
        {!!data?.overlapping_rows && <div className="merchant-dashboard-error" role="alert">{data.overlapping_rows} approved rows have overlapping periods and are excluded from totals. Review the reports before using these partial totals.</div>}
      </> : <div className="card merchant-permission-note">Financial report access is required to view Win/Lose and approval status.</div>}

      {showOverview && <>
        <div className="merchant-operations">
          <section className="card merchant-dashboard-section">
            <div className="merchant-section-heading"><div><h3>Game Provider Coverage</h3><p>Current configuration across all currencies.</p></div><button className="btn btn-secondary" onClick={onViewProducts}>View products</button></div>
            <div className="merchant-coverage">
              <div><strong>{configured.filter(item => item.status === 'active').length}</strong><span>Active connections</span></div>
              <div><strong>{configured.filter(item => item.status === 'draft').length}</strong><span>Draft connections</span></div>
              <div><strong>{new Set(configured.map(item => item.source_name).filter(name => name !== 'Not set')).size}</strong><span>Upstream sources</span></div>
            </div>
            <button type="button" className="merchant-gateway-summary" onClick={onViewPaymentGateways}>
              <span><strong>Payment Gateways <span className="merchant-count">({paymentGateways.length})</span></strong>
                <small>{paymentGateways.length ? `${paymentGateways.filter(item => item.status === 'active').length} active · ${paymentGateways.filter(item => item.status === 'draft').length} draft · ${paymentGateways.filter(item => item.status === 'inactive').length} inactive` : 'No payment gateways configured.'}</small>
              </span>
              <span>View gateways →</span>
            </button>
          </section>
          <section className="card merchant-dashboard-section">
            <div className="merchant-section-heading"><div><h3>Needs Attention</h3><p>Report approvals and merchant setup.</p></div></div>
            <div className="merchant-attention">
              {canViewFinance && <button onClick={onViewReports}><strong>{loading ? '…' : error ? '—' : pendingCount}</strong> reports pending approval <span>View →</span></button>}
              {canViewTickets && <details className="merchant-ticket-attention">
                <summary><strong>{ticketsLoading ? '…' : incompleteTickets.length}</strong> incomplete setup tickets</summary>
                {incompleteTickets.map(ticket => <button key={ticket._id} onClick={() => navigate(`/tickets/${ticket._id}`)}>{ticket.title || 'Setup ticket'}<span>View →</span></button>)}
              </details>}
              {!canViewFinance && !canViewTickets && <span className="merchant-muted">No accessible items.</span>}
            </div>
          </section>
        </div>

        <section className="card merchant-dashboard-section">
          <div className="merchant-section-heading"><div><h3>Product Win/Lose</h3><p>{periodLabel} · {selectedCurrency || 'No currency'} · Approved reports by product and upstream source.</p></div><button className="btn btn-secondary" onClick={onViewProducts}>Product details</button></div>
          <div className="merchant-table-scroll">
            <table className="table-striped merchant-dashboard-table">
              <thead><tr><th>Product</th><th>Upline / Source</th><th>Connection</th><th>Currency</th><th>BO Win/Lose</th><th>Provider Win/Lose</th><th>Difference</th></tr></thead>
              <tbody>{visibleProducts.map(product => (
                <tr key={`${product.master_provider_id}:${product.currency_code}`}>
                  <td><button className="merchant-product-link" onClick={() => onViewProducts(product.provider_name)}>{product.provider_name || product.master_provider_id}</button><small>{product.type || '—'}</small></td>
                  <td>{product.source_name || 'Not set'}<small>{product.is_client_line === undefined ? 'Historical connection' : product.is_client_line === null ? 'Line details restricted' : product.is_client_line ? 'Client line' : 'Company line'}</small></td>
                  <td><MerchantStatusBadge status={product.status} /></td><td>{product.currency_code}</td>
                  {['bo_winlose', 'provider_winlose', 'variance'].map(field => <td className={`merchant-amount${field === 'variance' && product.variance ? ' merchant-difference' : ''}`} key={field}>{canViewFinance && !loading && !error && product.row_count ? formatAmount(product[field]) : '—'}{field === 'variance' && !!product.overlapping_rows && <small>Overlapping rows excluded</small>}</td>)}
                </tr>
              ))}</tbody>
            </table>
            {!visibleProducts.length && <div className="merchant-empty">No products configured or reported in this currency.</div>}
          </div>
        </section>
      </>}
      {canViewFinance && !loading && !error && renderReports()}
    </div>
  );
};

export default MerchantFinancialDashboard;
